<template>
  <v-card
    v-bind="$attrs"
    :class="cardClasses"
    class="ds-card"
    :elevation="elevation"
    :variant="variant"
    :link="clickable"
    @click="handleClick"
  >
    <!-- Card Image -->
    <template v-if="image || $slots.image">
      <slot name="image">
        <v-img
          v-if="image"
          :src="image"
          :height="imageHeight"
          :cover="imageCover"
          :alt="imageAlt"
        />
      </slot>
    </template>

    <!-- Card Header -->
    <template v-if="title || subtitle || $slots.header">
      <v-card-title v-if="title || $slots.title">
        <slot name="title">
          <div class="ds-card__title">
            <ds-icon
              v-if="titleIcon"
              :ds-size="titleIconSize"
              :ds-color="titleIconColor"
              class="ds-card__title-icon"
            >
              {{ titleIcon }}
            </ds-icon>
            {{ title }}
          </div>
        </slot>
      </v-card-title>

      <v-card-subtitle v-if="subtitle || $slots.subtitle">
        <slot name="subtitle">
          {{ subtitle }}
        </slot>
      </v-card-subtitle>
    </template>

    <!-- Card Content -->
    <v-card-text v-if="description || $slots.content">
      <slot name="content">
        <p class="ds-card__description">{{ description }}</p>
      </slot>
    </v-card-text>

    <!-- Card Actions -->
    <v-card-actions v-if="$slots.actions || primaryAction || secondaryAction">
      <slot name="actions">
        <ds-button
          v-if="secondaryAction"
          ds-variant="ghost"
          @click.stop="handleSecondaryAction"
        >
          {{ secondaryActionText }}
        </ds-button>

        <v-spacer v-if="secondaryAction && primaryAction" />

        <ds-button
          v-if="primaryAction"
          :ds-variant="primaryActionVariant"
          @click.stop="handlePrimaryAction"
        >
          {{ primaryActionText }}
        </ds-button>
      </slot>
    </v-card-actions>

    <!-- Optional Badge/Chip -->
    <template v-if="badge || $slots.badge">
      <div class="ds-card__badge">
        <slot name="badge">
          <v-chip
            v-if="badge"
            :color="badgeColor"
            :size="badgeSize"
            :variant="badgeVariant"
          >
            {{ badge }}
          </v-chip>
        </slot>
      </div>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { DsButton, DsIcon } from '@/shared/components/atoms'

export interface DsCardProps {
  /**
   * Título de la tarjeta
   */
  title?: string
  /**
   * Subtítulo de la tarjeta
   */
  subtitle?: string
  /**
   * Descripción/contenido de la tarjeta
   */
  description?: string
  /**
   * URL de la imagen
   */
  image?: string
  /**
   * Texto alternativo de la imagen
   */
  imageAlt?: string
  /**
   * Altura de la imagen
   * @default '200'
   */
  imageHeight?: string | number
  /**
   * Si la imagen debe cubrir todo el espacio
   * @default true
   */
  imageCover?: boolean
  /**
   * Icono del título
   */
  titleIcon?: string
  /**
   * Tamaño del icono del título
   * @default 'md'
   */
  titleIconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /**
   * Color del icono del título
   * @default 'primary'
   */
  titleIconColor?: 'primary' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'gray'
  /**
   * Variante de la tarjeta
   * @default 'elevated'
   */
  variant?: 'flat' | 'tonal' | 'outlined' | 'text' | 'elevated'
  /**
   * Elevación de la tarjeta
   * @default 2
   */
  elevation?: number | string
  /**
   * Si la tarjeta es clickeable
   * @default false
   */
  clickable?: boolean
  /**
   * Si la tarjeta tiene hover effect
   * @default false
   */
  hoverable?: boolean
  /**
   * Texto de la acción primaria
   */
  primaryActionText?: string
  /**
   * Variante de la acción primaria
   * @default 'primary'
   */
  primaryActionVariant?: 'primary' | 'secondary' | 'ghost' | 'text'
  /**
   * Si hay acción primaria
   * @default false
   */
  primaryAction?: boolean
  /**
   * Texto de la acción secundaria
   */
  secondaryActionText?: string
  /**
   * Si hay acción secundaria
   * @default false
   */
  secondaryAction?: boolean
  /**
   * Badge/etiqueta de la tarjeta
   */
  badge?: string
  /**
   * Color del badge
   * @default 'primary'
   */
  badgeColor?: string
  /**
   * Tamaño del badge
   * @default 'small'
   */
  badgeSize?: 'x-small' | 'small' | 'default' | 'large' | 'x-large'
  /**
   * Variante del badge
   * @default 'tonal'
   */
  badgeVariant?: 'flat' | 'tonal' | 'outlined' | 'text' | 'elevated'
}

const props = withDefaults(defineProps<DsCardProps>(), {
  imageHeight: '200',
  imageCover: true,
  titleIconSize: 'md',
  titleIconColor: 'primary',
  variant: 'elevated',
  elevation: 2,
  clickable: false,
  hoverable: false,
  primaryActionVariant: 'primary',
  primaryAction: false,
  secondaryAction: false,
  badgeColor: 'primary',
  badgeSize: 'small',
  badgeVariant: 'tonal'
})

const emit = defineEmits<{
  'click': []
  'primary-action': []
  'secondary-action': []
}>()

const slots = useSlots()

// Card classes
const cardClasses = computed(() => ({
  'ds-card--clickable': props.clickable,
  'ds-card--hoverable': props.hoverable,
  'ds-card--with-image': props.image || !!slots.image,
  'ds-card--with-badge': props.badge || !!slots.badge
}))

// Handlers
const handleClick = () => {
  if (props.clickable) {
    emit('click')
  }
}

const handlePrimaryAction = () => {
  emit('primary-action')
}

const handleSecondaryAction = () => {
  emit('secondary-action')
}
</script>

<style scoped lang="scss">
.ds-card {
  position: relative;
  transition: all 0.3s ease;

  &:not(.ds-card--clickable) {
    cursor: default;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }

  &__title-icon {
    flex-shrink: 0;
  }

  &__description {
    line-height: 1.6;
    color: rgb(var(--v-theme-gray-700));
  }

  &__badge {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
  }

  // Clickable state
  &--clickable {
    cursor: pointer;

    &:active {
      transform: scale(0.98);
    }
  }

  // Hoverable state
  &--hoverable {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
  }

  // With image
  &--with-image {
    .v-card-title {
      padding-top: 16px;
    }
  }
}
</style>
